import { CommandIndexProvidersQuery } from '~/graphql/__generated__/graphql';
import { VisitResourceRequirement } from '~/providers/OrgConfigProvider';

export interface CareTeamProvider {
  id: number;
  providerId: number;
  providerTypeId: number;
}

/**
 * Checks if care team providers are missing from the selected providers
 * @param careTeamProviders - Array of care team providers for the patient
 * @param selectedProviders - Record mapping requirement ID to selected provider ID
 */
export const isMissingCareTeam = ({
  careTeamProviders,
  selectedProviders
}: {
  careTeamProviders: { id: number }[] | undefined;
  selectedProviders: Record<number, number>;
}): boolean => {
  if (!careTeamProviders || careTeamProviders.length === 0) return false;

  // If no providers are selected at all, we're not missing care team
  const selectedProviderValues = Object.values(selectedProviders);
  if (selectedProviderValues.length === 0) return false;

  // filters out -1 (explicitly none from SelectProvider assignment) and 0/falsy values
  const assignedProviderIds = selectedProviderValues.filter(id => id && (id !== -1));

  // If all selections are explicitly "none" (-1), we're not missing care team
  if (assignedProviderIds.length === 0) return false;

  const filteredCareTeam = careTeamProviders.filter(ctp => {
    return assignedProviderIds.some(id => id === ctp?.id);
  });

  return filteredCareTeam.length === 0;
};

/**
 * Returns the provider type IDs that are allowed to be used with the care team from visit resource requirements
 * @param visitResourceRequirements - Visit resource requirements
 * @returns Provider type IDs
 */
const allowedProviderTypeIdsWithCareTeam = (visitResourceRequirements: VisitResourceRequirement[]) => {
  return visitResourceRequirements.filter(vrr => vrr.requiresContinuityOfCare)
    .flatMap(vrr => vrr.providerTypes.map(pt => pt.id));
};

export const filterOutUndefined = <T>(a: T | undefined | null): a is T => !!a

/**
 * Returns the care team providers that are allowed to be used with the visit resource requirements
 * @param careTeamProviders - Care team providers
 * @param providerTypeIds - Provider type IDs
 * @returns Care team providers
 */
export const getAllowedCareTeamProviders = (
  visitResourceRequirements: VisitResourceRequirement[],
  careTeamProviders: CareTeamProvider[],
  providerData: CommandIndexProvidersQuery["commandIndexProviders"]["providers"]
) => {
  const providerTypeIds = allowedProviderTypeIdsWithCareTeam(visitResourceRequirements);
  const patientCareTeamData = careTeamProviders?.filter((ctp) => providerTypeIds?.includes(ctp.providerTypeId)) || [];

  return patientCareTeamData.map((ctp) => (providerData.find((p) => p.id === ctp.providerId))).filter(filterOutUndefined);
};

/**
 * Applies care team providers to provider data based on availability and type compatibility
 * @param providerData - Current provider data array
 * @param careTeamProviders - Available care team providers
 * @param visitResourceRequirements - Visit resource requirements
 * @param availableProvidersBySlot - Available providers for each requirement slot (filtered by shift/time)
 */
export const applyCareTeamToProviderData = ({
  providerData,
  careTeamProviders,
  visitResourceRequirements,
  availableProvidersBySlot
}: {
  providerData: Array<{ _vrrId: number; resourceId: string; offset: number; duration: number }>;
  careTeamProviders: { id: number, providerTypeId: number }[] | undefined;
  visitResourceRequirements: VisitResourceRequirement[] | undefined;
  availableProvidersBySlot: Array<Array<{ value: number; label: string }>>;
}): Array<{ _vrrId: number; resourceId: string; offset: number; duration: number }> => {
  if (!careTeamProviders?.length || !visitResourceRequirements?.length) {
    return providerData;
  }

  const newProviderData = [...providerData];
  
  visitResourceRequirements.forEach((requirement, index) => {
    // Get the available providers for this specific requirement (filtered)
    const availableProvidersForThisSlot = availableProvidersBySlot[index] || [];
    const availableProviderIds = availableProvidersForThisSlot.map(p => p.value);

    const allowedProviderTypesIds = requirement.providerTypes.map(pt => pt.id);
    
    // Find the first care team provider that matches this requirement AND is available
    const matchingCareTeamProvider = careTeamProviders.find(provider => 
      provider.providerTypeId && 
      allowedProviderTypesIds.includes(provider.providerTypeId) &&
      availableProviderIds.includes(provider.id)
    );
    
    // Find or create the provider data entry for this requirement
    const providerDataIndex = newProviderData.findIndex(pd => pd._vrrId === requirement.id);
    
    if (providerDataIndex === -1) {
      // Create new entry if it doesn't exist
      newProviderData.push({
        _vrrId: requirement.id,
        resourceId: matchingCareTeamProvider ? matchingCareTeamProvider.id.toString() : '',
        offset: 0,
        duration: 0
      });
    } else if (matchingCareTeamProvider) {
      // Update existing entry
      newProviderData[providerDataIndex].resourceId = matchingCareTeamProvider.id.toString();
    }
  });
  
  return newProviderData;
};
